import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Input, Select } from '../../components/ui/Input';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import { repositorioTipos } from '../../../data/repositories/RepositorioTipos';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { Icon } from '../../components/ui/Icon';
import { Modal } from '../../components/ui/Modal';
import { useSesionStore } from '../../stores/sesionStore';
import estilos from '../../components/layout/PaginaCrud.module.css';

interface FilaTipo {
  clave: string;
  tipoId: number;
  nombre: string;
  categoria: string | null;
  clienteId: number | null;
  clienteNombre: string;
  habilitado: boolean;
}

/**
 * Tipos de actuador habilitados por cliente.
 *
 * No es un catálogo que se cree y se borre: los tipos existen a nivel de
 * sistema y aquí se decide cuáles puede usar cada cliente. Por eso la única
 * acción es activar o desactivar.
 */
export function TiposActuadores() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_catalogos') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar los catálogos';

  const [busqueda, setBusqueda] = useState('');
  const [clienteId, setClienteId] = useState<number | null>(null);
  const [soloHabilitados, setSoloHabilitados] = useState<'todos' | 'si' | 'no'>('todos');
  const [agregando, setAgregando] = useState(false);

  const tipos = useQuery({
    queryKey: ['tipos', 'actuadores'],
    queryFn: () => repositorioTipos.tiposDeActuadorPorCliente(),
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['tipos', 'actuadores'] });
  };

  const alternar = useMutation({
    mutationFn: ({
      clienteId: cliente,
      tipoId,
      habilitado,
    }: {
      clienteId: number;
      tipoId: number;
      habilitado: boolean;
    }) => repositorioTipos.cambiarHabilitadoTipoActuador(cliente, tipoId, habilitado),
    onSuccess: () => {
      toast.success('Cambio guardado');
      refrescar();
    },
    onError: (e) => {
      registrarError('tiposActuadores.alternar', e);
      toast.error('No se pudo cambiar la habilitación');
    },
  });

  const quitar = useMutation({
    mutationFn: ({ clienteId: cliente, tipoId }: { clienteId: number; tipoId: number }) =>
      repositorioTipos.quitarTipoActuadorDeCliente(cliente, tipoId),
    onSuccess: () => {
      toast.success('Tipo quitado del cliente');
      refrescar();
    },
    onError: (e) => {
      registrarError('tiposActuadores.quitar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo quitar el tipo');
    },
  });

  const nombresCliente = useMemo(() => {
    const mapa = new Map<number, string>();
    for (const c of clientes.data ?? []) mapa.set(c.id, c.nombre);
    return mapa;
  }, [clientes.data]);

  const lista = useMemo<FilaTipo[]>(
    () =>
      (tipos.data ?? []).map((t) => ({
        // La combinación de tipo y cliente es lo que identifica a una fila.
        clave: `${t.id}-${t.clienteId ?? 'global'}`,
        tipoId: t.id,
        nombre: t.nombre,
        categoria: t.categoria,
        clienteId: t.clienteId,
        clienteNombre:
          t.clienteId !== null
            ? (nombresCliente.get(t.clienteId) ?? `Cliente ${t.clienteId}`)
            : '—',
        habilitado: t.habilitado,
      })),
    [tipos.data, nombresCliente],
  );

  const filtrados = useMemo(
    () =>
      lista.filter((t) => {
        if (clienteId !== null && t.clienteId !== clienteId) return false;
        if (soloHabilitados === 'si' && !t.habilitado) return false;
        if (soloHabilitados === 'no' && t.habilitado) return false;

        const q = busqueda.trim().toLowerCase();
        return (
          !q || `${t.nombre} ${t.categoria ?? ''} ${t.clienteNombre}`.toLowerCase().includes(q)
        );
      }),
    [lista, busqueda, clienteId, soloHabilitados],
  );

  const columnas: Columna<FilaTipo>[] = [
    {
      clave: 'nombre',
      encabezado: 'Tipo de actuador',
      movil: 'titulo',
      render: (t) => <span className={estilos.nombre}>{t.nombre}</span>,
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      movil: 'subtitulo',
      render: (t) => t.clienteNombre,
    },
    {
      clave: 'categoria',
      encabezado: 'Categoría',
      render: (t) =>
        t.categoria ? (
          <Badge color="var(--text-2)">{t.categoria}</Badge>
        ) : (
          <span className={estilos.secundario}>—</span>
        ),
    },
    {
      clave: 'habilitado',
      encabezado: 'Disponible',
      render: (t) =>
        t.habilitado ? (
          <Badge color="var(--status-success)">Sí</Badge>
        ) : (
          <Badge color="var(--text-3)">No</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (t) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={t.habilitado ? 'interruptorOn' : 'interruptorOff'}
            title={t.habilitado ? 'Quitar del cliente' : 'Habilitar para el cliente'}
            motivoDeshabilitado={
              sinPermiso ??
              (t.clienteId === null ? 'Este tipo no está asignado a un cliente' : null)
            }
            onClick={() => {
              if (t.clienteId === null) return;

              // Quitar un tipo deja sin poder accionarse a los equipos que ya
              // lo usan, así que conviene decirlo antes.
              if (t.habilitado) {
                const seguro = window.confirm(
                  `¿Quitar «${t.nombre}» de ${t.clienteNombre}?\n\n` +
                    'Los actuadores de ese tipo dejarán de poder accionarse.',
                );
                if (!seguro) return;
              }

              alternar.mutate({
                clienteId: t.clienteId,
                tipoId: t.tipoId,
                habilitado: !t.habilitado,
              });
            }}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Quitar el tipo de este cliente"
            motivoDeshabilitado={
              sinPermiso ??
              (t.clienteId === null ? 'Este tipo no está asignado a un cliente' : null)
            }
            onClick={() => {
              if (t.clienteId === null) return;

              const seguro = window.confirm(
                `¿Quitar «${t.nombre}» de ${t.clienteNombre}?

` + 'Desaparecerá de su lista. Si tiene actuadores usándolo, el servidor lo impedirá.',
              );
              if (seguro) quitar.mutate({ clienteId: t.clienteId, tipoId: t.tipoId });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Tipos de actuadores"
        descripcion="Qué clases de equipo puede usar cada cliente."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={tipos.isFetching}
            >
              Actualizar
            </Button>
            <Button
              icono="agregar"
              compactoEnMovil
              motivoDeshabilitado={sinPermiso}
              onClick={() => setAgregando(true)}
            >
              Dar un tipo a un cliente
            </Button>
          </>
        }
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          placeholder="Tipo, categoría o cliente"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteId ?? ''}
          onChange={(e) => setClienteId(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
        <Select
          etiqueta="Disponible"
          value={soloHabilitados}
          onChange={(e) => setSoloHabilitados(e.target.value as typeof soloHabilitados)}
          opciones={[
            { valor: 'todos', etiqueta: 'Todos' },
            { valor: 'si', etiqueta: 'Solo disponibles' },
            { valor: 'no', etiqueta: 'Solo no disponibles' },
          ]}
        />
      </BarraFiltros>

      {tipos.isLoading ? (
        <ListaEsqueleto filas={5} />
      ) : tipos.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="categoria"
          titulo={lista.length === 0 ? 'Sin tipos de actuador' : 'Ninguno coincide'}
          descripcion={
            lista.length === 0
              ? 'Los tipos de actuador se dan de alta a nivel de sistema y luego se habilitan por cliente.'
              : 'Prueba a cambiar la búsqueda o los filtros.'
          }
        />
      ) : (
        <Tabla
          datos={filtrados}
          columnas={columnas}
          claveFila={(t) => t.clave}
          etiqueta="Tipos de actuadores por cliente"
        />
      )}

      <AgregarTipoACliente
        abierto={agregando}
        clientes={(clientes.data ?? []).map((c) => ({ id: c.id, nombre: c.nombre }))}
        onCerrar={() => setAgregando(false)}
        onGuardado={() => {
          setAgregando(false);
          refrescar();
        }}
      />
    </div>
  );
}

interface AgregarTipoProps {
  abierto: boolean;
  clientes: readonly { id: number; nombre: string }[];
  onCerrar: () => void;
  onGuardado: () => void;
}

/**
 * Da a un cliente un tipo del catálogo global.
 *
 * La lista de tipos elegibles la decide el servidor: sólo ofrece los que el
 * cliente todavía no tiene, así que aquí no hay nada que deducir.
 */
function AgregarTipoACliente({ abierto, clientes, onCerrar, onGuardado }: AgregarTipoProps) {
  const [clienteId, setClienteId] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [activar, setActivar] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!abierto) return;

    setClienteId('');
    setTipoId('');
    setActivar(true);
    setError(null);
  }, [abierto]);

  const disponibles = useQuery({
    queryKey: ['tipos', 'actuadores', 'disponibles', clienteId],
    queryFn: () => repositorioTipos.tiposActuadorDisponibles(Number(clienteId)),
    enabled: abierto && clienteId !== '',
  });

  const agregar = useMutation({
    mutationFn: () =>
      repositorioTipos.agregarTipoActuadorACliente(Number(clienteId), Number(tipoId), activar),
    onSuccess: () => {
      toast.success('Tipo agregado al cliente');
      onGuardado();
    },
    onError: (e) => {
      registrarError('tiposActuadores.agregar', e);
      setError(e instanceof ErrorApp ? e.message : 'No se pudo agregar el tipo');
    },
  });

  const enviar = () => {
    setError(null);

    if (!clienteId) {
      setError('Elige a qué cliente se le da el tipo.');
      return;
    }
    if (!tipoId) {
      setError('Elige qué tipo darle.');
      return;
    }

    agregar.mutate();
  };

  const sinDisponibles =
    clienteId !== '' && !disponibles.isLoading && (disponibles.data ?? []).length === 0;

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Dar un tipo a un cliente"
      descripcion="El cliente podrá crear actuadores de ese tipo en sus nodos."
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button icono="guardar" cargando={agregar.isPending} onClick={enviar}>
            Agregar
          </Button>
        </>
      }
    >
      <div className={estilos.formulario}>
        {error && (
          <div className={estilos.error}>
            <Icon nombre="errorContorno" tamano={15} />
            {error}
          </div>
        )}

        <Select
          etiqueta="Cliente"
          requerido
          placeholder="Elige un cliente"
          value={clienteId}
          onChange={(e) => {
            setClienteId(e.target.value);
            setTipoId('');
          }}
          opciones={clientes.map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />

        <Select
          etiqueta="Tipo de actuador"
          requerido
          placeholder={
            clienteId === ''
              ? 'Elige un cliente primero'
              : disponibles.isLoading
                ? 'Cargando…'
                : sinDisponibles
                  ? 'Ya tiene todos los tipos del catálogo'
                  : 'Elige un tipo'
          }
          value={tipoId}
          disabled={clienteId === '' || sinDisponibles}
          onChange={(e) => setTipoId(e.target.value)}
          opciones={(disponibles.data ?? []).map((t) => ({ valor: t.id, etiqueta: t.nombre }))}
        />

        <label className={estilos.opcion}>
          <input type="checkbox" checked={activar} onChange={(e) => setActivar(e.target.checked)} />
          <span>
            Dejarlo disponible ya
            <span className={estilos.ayuda}>
              Si lo desmarcas, el tipo queda asignado pero apagado hasta que se habilite.
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
